import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ShieldCheck } from 'lucide-react';
import { ERROR_CLS } from '@/components/brand/BrandKit';
import { AuthShell, AuthLink, Field, PasswordField } from '@/components/account/AuthShell';
import { resetPassword, apiError } from '@/api/account';

export default function ResetPasswordPage() {
  const { token } = useParams();
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => { document.title = 'Choose a new password | Eurogulf Mobility Group'; }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (password !== confirm) { setError('The two passwords do not match.'); return; }
    setError('');
    setLoading(true);
    try {
      await resetPassword(token, password);
      setDone(true);
    } catch (err) {
      setError(apiError(err, 'Could not reset your password. Please request a new link.'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthShell testid="reset-page" eyebrow="Password help" title="Choose a new password" lead="Your new password must be at least 8 characters, with a letter and a number."
      footer={!done && <>Link expired? <AuthLink to="/forgot-password" testid="reset-to-forgot">Request a new one</AuthLink></>}>
      {done ? (
        <div data-testid="reset-success" className="text-center py-4">
          <span className="mx-auto grid h-16 w-16 place-items-center rounded-full bg-[#FFF4ED] text-[#EE5A01]"><ShieldCheck className="w-8 h-8" /></span>
          <h2 className="mt-5 font-heading font-black text-2xl text-[#121212]">Password updated</h2>
          <p className="mt-2 font-body text-sm text-[#666666]">You can now sign in with your new password.</p>
          <Link to="/login" data-testid="reset-to-login" className="btn-primary mt-6 inline-flex">Sign in</Link>
        </div>
      ) : (
        <form onSubmit={submit} className="space-y-5" noValidate>
          {error && <p data-testid="reset-error" role="alert" className={ERROR_CLS}>{error}</p>}
          <Field label="New password" htmlFor="reset-password">
            <PasswordField id="reset-password" testid="reset-password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="New password" autoComplete="new-password" />
          </Field>
          <Field label="Confirm new password" htmlFor="reset-confirm">
            <PasswordField id="reset-confirm" testid="reset-confirm" value={confirm} onChange={(e) => setConfirm(e.target.value)} placeholder="Repeat new password" autoComplete="new-password" />
          </Field>
          <button type="submit" data-testid="reset-submit" disabled={loading || !password || !confirm} className="btn-primary w-full disabled:opacity-50">
            {loading ? 'Saving…' : 'Save new password'}
          </button>
        </form>
      )}
    </AuthShell>
  );
}
